"use client";

import { isFilled } from "@prismicio/client";
import { PrismicLink } from "@prismicio/react";
import { clsx } from "clsx";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useIntersectionObserver, useWindowSize } from "usehooks-ts";

import { modifyQueryParams } from "../modify-query-params";

import type { LinkField } from "@prismicio/client";
import type { ComponentType } from "react";

export interface VimeoPlayerProps {
  autoplay: boolean;
  id: string;
  finishedCallback?: () => void;
  shouldLoad?: boolean;
}

const TAILWIND_XL_WIDTH = 1280;

const VIDEO_THUMBNAIL_CLASS =
  "video-list-item-thumbnail w-full shrink-0 xl:mr-3 xl:h-[56px] xl:w-[100px] xl:min-w-[100px] bg-gray-200";

export type Video = {
  duration: string;
  hidden: boolean;
  video_id: string;
  thumbnail_url: string;
  title: string;
};

export type DecoratedVideo = {
  duration: string;
  hidden: boolean;
  video_id: string;
  video_id_ad?: string;
  selected: boolean;
  thumbnail_url: string;
  title: string;
  link?: LinkField;
};

export type VideoCarouselProps = {
  styles?: Record<string, (...rest: Array<unknown>) => string>;
  theme: string;
  videos: Array<Video>;
  VideoPlayerComponent: ComponentType<VimeoPlayerProps>;
};

const LIST_STYLES = clsx(
  "overflow-x-auto flex overflow-y-hidden ",
  "xl:snap-none xl:block xl:w-[40%] xl:overflow-y-auto xl:overflow-x-hidden xl:pl-[4%]",
);

const ITEM_STYLES = clsx(
  "my-6 mr-6 w-[40%] min-w-[40%] text-sm",
  "lg:w-[30%] lg:min-w-[30%]",
  "xl:m-0 xl:w-full xl:min-w-full xl:text-base",
);

export function VideoCarousel(props: VideoCarouselProps) {
  return (
    <Suspense>
      <VideoCarouselInner {...props} />
    </Suspense>
  );
}

const VideoCarouselInner = function VideoCarouselInner({
  videos,
  VideoPlayerComponent,
}: VideoCarouselProps) {
  const windowSize = useWindowSize();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const iframeWrapperRef = useRef(null);
  const listRef = useRef(null);
  const {
    entry,
    isIntersecting,
    ref: playerRef,
  } = useIntersectionObserver({
    freezeOnceVisible: true,
  });

  const [autoplay, setAutoPlay] = useState(false);

  const decoratedVideos: Array<DecoratedVideo> = useMemo(
    function () {
      const videoQuery = searchParams.get("video") || undefined;

      const videoIsHidden = function (v: Video) {
        return v.hidden
          ? searchParams.get("video") === v.video_id
            ? false
            : true
          : false;
      };

      const videoIsSelected = function (v: Video) {
        if (!videoQuery) {
          return v.video_id === videos[0]["video_id"];
        } else {
          return v["video_id"] === videoQuery;
        }
      };

      return videos.map((v: Video) => {
        return {
          ...v,
          hidden: videoIsHidden(v),
          selected: videoIsSelected(v),
        };
      });
    },
    [searchParams, videos],
  );

  const selectedVideoID = useMemo(
    function () {
      const videoQuery = searchParams.get("video") || undefined;

      // Use the query parameter directly if present (supports both video_id and video_id_ad)
      if (videoQuery) {
        return videoQuery;
      }

      // Otherwise, fall back to the first video
      return videos[0]["video_id"];
    },
    [searchParams, videos],
  );

  const selectedVideo = decoratedVideos.find((video) => {
    return video.video_id === selectedVideoID;
  });

  const baseURL = pathname;

  const setSelectedVideo = function (id: string) {
    const params = new URLSearchParams(searchParams.toString());
    params.set("video", id);
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
  };

  useEffect(
    function handleResize() {
      if (!listRef.current || !iframeWrapperRef.current || !windowSize.width) {
        return;
      }

      const $list = listRef.current as HTMLUListElement;
      const $wrapper = iframeWrapperRef.current as HTMLDivElement;

      if (windowSize.width >= TAILWIND_XL_WIDTH) {
        $list.style.height =
          Math.max($wrapper.getBoundingClientRect().height, 350) + "px";
      } else {
        $list.style.height = "";
      }
    },
    [windowSize, listRef, iframeWrapperRef],
  );

  useEffect(
    function () {
      const scrollToPlayer = function () {
        const $player = entry!.target as HTMLDivElement;
        window.scrollTo({
          top: $player.getBoundingClientRect().top - 130 + window.scrollY,
          behavior: "smooth",
        });
      };

      const videoQuery = searchParams.get("video") || undefined;
      const foundVideo = decoratedVideos.find((video) => {
        return video.video_id === videoQuery;
      });

      if (foundVideo && entry?.target) {
        const wrapper = document.querySelector(".video-carousel-wrapper");
        if (wrapper) {
          wrapper.classList.remove("hidden");
        }
        scrollToPlayer();
        setAutoPlay(true);
      }
    },
    [searchParams, decoratedVideos, entry],
  );

  const visibleVideos = videos.filter((v) => {
    const videoQuery = searchParams.get("video") || undefined;
    if (!v.hidden) {
      return true;
    }
    if (videoQuery === v.video_id) {
      return true;
    }
    return false;
  });

  return (
    <div className="xl:flex" ref={playerRef} data-test-id="video-carousel">
      <div
        className={`xl:w-[60%] ${visibleVideos.length > 1 ? "" : "mx-auto"}`}
      >
        <div className="relative aspect-video" ref={iframeWrapperRef}>
          {selectedVideoID && (
            <VideoPlayerComponent
              autoplay={autoplay}
              id={selectedVideoID}
              shouldLoad={isIntersecting}
            />
          )}
        </div>
        <div
          className={
            selectedVideo?.video_id_ad || selectedVideo?.link ? "pt-1" : ""
          }
        >
          {selectedVideo?.video_id_ad ? (
            <div className="mt-1" ref={iframeWrapperRef}>
              <button
                className="underline opacity-50"
                onClick={() => {
                  setSelectedVideo(selectedVideo.video_id_ad as string);
                }}
              >
                Extended audio description
              </button>
            </div>
          ) : null}
          {isFilled.link(selectedVideo?.link) ? (
            <div className="mt-1">
              <PrismicLink
                className="underline opacity-50"
                field={selectedVideo.link}
                target="_blank"
                rel="noopener noreferrer"
              />
            </div>
          ) : null}
        </div>
      </div>
      {visibleVideos.length > 1 ? (
        <ul className={LIST_STYLES} data-test-id="video-list" ref={listRef}>
          {decoratedVideos.map((video) => {
            return (
              <li
                className={`video-list-item ${
                  video.selected ? "selected" : ""
                } ${ITEM_STYLES} ${video.hidden ? "hidden" : ""}`}
                data-test-id="video-list-item"
                key={video.video_id}
              >
                <a
                  className={`video-list-item-button block w-full text-left xl:flex xl:p-4 xl:items-start ${
                    video.selected ? "selected" : ""
                  }`}
                  data-test-id="video-list-item-button"
                  href={`${baseURL}?video=${video.video_id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedVideo(video.video_id);
                  }}
                >
                  <div className="mb-4 flex items-center xl:mb-0">
                    {video.thumbnail_url ? (
                      <img
                        alt="Video preview"
                        className={VIDEO_THUMBNAIL_CLASS}
                        data-test-id="video-list-item-thumbnail"
                        src={modifyQueryParams(video.thumbnail_url, {
                          q: 40,
                          mw: 320,
                          mh: 180,
                        }).toString()}
                        width="320"
                        height="180"
                      />
                    ) : (
                      <div className={VIDEO_THUMBNAIL_CLASS} />
                    )}
                  </div>

                  <div
                    className="video-list-item-text"
                    data-test-id="video-list-item-text"
                  >
                    {video.title}
                    <br />({video.duration})
                  </div>
                </a>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};
